import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchApi } from "@/lib/api/index.ts";
import { User } from "@/lib/api/user.ts";

interface CreateCallSchema {
  sdk_id: string;
  to_user: string;
}

enum CALL_STATUS {
  NEW = "new",
  ENDED = "ended",
  SCHEDULED = "scheduled",
  CANCELLED = "cancelled",
}

export interface Call {
  id: string;
  sdk_id: string;
  sendbird_room_id: string;
  start: Date;
  duration: number;
  notif_str?: string;
  started_by: string;
  host?: string;
  fromUser: User;
  toUser: User;
  mentee_rating?: string;
  mentee_note?: string;
  special?: string;
  recording_url?: string;
  status: CALL_STATUS;
  at_least_one_has_joined: boolean;
  created_at: string;
  updated_at: string;
}

export const useActiveCall = () => {
  return useQuery({
    queryKey: ["calls-active"],
    queryFn: async () => {
      const call = await fetchApi<{ call: Call }>({
        url: "/v1/calls/active",
      });

      if (!call.data?.call) return null;

      return call.data?.call;
    },
    refetchIntervalInBackground: true,
  });
};

export const useCreateCall = () => {
  return useMutation<Call, Error, CreateCallSchema>({
    mutationKey: ["calls-create"],
    mutationFn: async (data) => {
      const call = await fetchApi<{ call: Call }>({
        url: "/v1/calls",
        method: "POST",
        data,
      });

      if (!call.data) throw new Error("Call not created");

      return call.data.call;
    },
  });
};

export const useEndCall = () => {
  return useMutation<void, Error, { callId: string }>({
    mutationKey: ["calls-end"],
    mutationFn: async ({ callId }) => {
      await fetchApi({
        url: `/v1/calls/${callId}/end`,
        method: "PUT",
      });
    },
  });
};

export const useGetCallLogs = (mentorshipId?: string) => {
  return useQuery({
    queryKey: ["calls-logs", mentorshipId],
    queryFn: async () => {
      const logs = await fetchApi<{ calls: Call[] }>({
        url: "/v1/calls/logs",
        params: {
          mentorshipId,
        },
      });

      if (!logs.data?.calls) return [];

      return logs.data.calls;
    },
    enabled: !!mentorshipId,
  });
};

export const useBackupCall = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { callId: string }>({
    mutationKey: ["calls-backup"],
    mutationFn: async ({ callId }) => {
      await fetchApi({
        url: `/v1/calls/${callId}/backup`,
        method: "POST",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey?.[0] === "calls-logs",
      });
    },
  });
};
